import { defineStore } from 'pinia'
import { ref } from 'vue'
import { apiConfig } from '../config/apiConfig'
import { logger } from '../utils/logger'
import { GoogleAuthProvider, onAuthStateChanged } from 'firebase/auth';
import { ref as storageRef, getBlob, list } from 'firebase/storage';
import { storage, auth, db } from '../firebase';
import { initializeDuckDB as _initDuckDB, getConnection as _getConnection, queryDuckDB as _queryDuckDB, closeConnection } from './duckDBManager';

import {
  doc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore'

import { cacheParquetFile, getAllCachedFiles, deleteCachedFiles, clearCachedFilesByType, clearIndexedDBCache } from './indexedDBCache'

import { escapeSql } from '../utils/escapeSql';
import { planParquetCacheSync } from '../utils/planParquetCacheSync';
export { escapeSql };

/**
 * Store for managing user authentication and DuckDB data access
 */
export const useUserStore = defineStore('user', () => {

  const googleAuthProvider = new GoogleAuthProvider()
  const currentUser = ref(auth.currentUser);
  // Keep currentUser reactive with Firebase auth state to avoid stale tokens
  // Store unsubscribe to prevent listener accumulation on HMR
  let _authUnsub = null;
  const setupAuthListener = () => {
    if (_authUnsub) _authUnsub();
    _authUnsub = onAuthStateChanged(auth, (user) => {
      currentUser.value = user;
    });
  };
  setupAuthListener();

  const loading = ref(false)
  const error = ref(null)

  const connectionStatus = ref({
    'data@definemg.com': {
      valid: false,
      checking: true,
      scopes: [],
      lastChecked: null,
      expiryDate: null
    },
    'definemg@gmail.com': {
      valid: false,
      checking: true,
      scopes: [],
      lastChecked: null,
      expiryDate: null
    }
  })


  /**
   * Makes an authenticated API request
   * @param {string} url - The API endpoint URL
   * @param {string} method - The HTTP method ('GET' or 'POST')
   * @param {Object} [data] - Optional data to send with POST requests
   * @returns {Promise<any>} The parsed JSON response
   * @throws {Error} If the request fails or user is not authenticated
   */
  const fetchWithAuth = async (url, method = 'GET', data = null) => {
    loading.value = true
    error.value = null


    try {
      const buildOptions = async (forceRefresh = false) => {
        const token = await auth.currentUser?.getIdToken(forceRefresh)
        if (!token) throw new Error('Not authenticated')
        const options = {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
        if (data && method === 'POST') {
          options.body = JSON.stringify(data)
        }
        return options
      }

      // Construct full URL if it's a relative path
      const fullUrl = url.startsWith('http')
        ? url
        : `${apiConfig.baseUrl}${url.startsWith('/') ? url : `/${url}`}`

      // First attempt with cached token
      let response = await fetch(fullUrl, await buildOptions(false))
      // If token expired or unauthorized, retry once with forced refresh
      if (response.status === 401 || response.status === 403) {
        try {
          const bodyText = await response.text().catch(() => '')
          const looksExpired = bodyText && bodyText.toLowerCase().includes('id token has expired')
          if (looksExpired || response.status === 401 || response.status === 403) {
            response = await fetch(fullUrl, await buildOptions(true))
          }
        } catch (_) {
          // Ignore parsing errors and proceed with refreshed attempt
          response = await fetch(fullUrl, await buildOptions(true))
        }
      }

      if (!response.ok) {
        let errorMessage = `API call failed: ${response.status} ${response.statusText}`
        try {
          const errorData = await response.json()
          // The shared error handler nests it: { error: { message } }
          if (errorData?.error) errorMessage = errorData.error.message || errorData.error
        } catch (_) {
          // non-JSON response
        }
        throw new Error(errorMessage)
      }

      return await response.json()
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  const signIn = async () => {
    error.value = null
    loading.value = true
    let authResult = null

    try {
      // First authenticate with Google
      const { signInWithPopup } = await import('firebase/auth');
      authResult = await signInWithPopup(auth, googleAuthProvider)

      // Set current user immediately to get token for fetchWithAuth
      currentUser.value = authResult.user
      // Now check authorization
      const result = await fetchWithAuth('/check-email-authorization', 'POST', {
        email: authResult.user.email
      })
      
      logger.log('email authorization result', result)
      if (!result?.isAuthorized) {
        throw new Error('Email not authorized')
      }

      // Create/update user document in Firestore
      const userRef = doc(db, 'users', authResult.user.email)

      await setDoc(userRef, {
        email: authResult.user.email,
        displayName: authResult.user.displayName,
        photoURL: authResult.user.photoURL,
        uid: authResult.user.uid,
        lastLogin: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      }, { merge: true })

      // Initialize DuckDB (without loading data) after successful authentication
      try {
        await initializeDuckDB();
      } catch (dbErr) {
        logger.error('DuckDB initialization failed:', dbErr);
        // Don't fail the sign-in if DuckDB fails
      }

      return true
    } catch (err) {
      logger.error('Failed to sign in:', err)
      error.value = err.message || 'Failed to sign in'

      // Clean up if anything fails
      if (authResult?.user) {
        try {
          await auth.signOut()
          currentUser.value = null
        } catch (e) {
          logger.error('Error cleaning up after failed sign in:', e)
        }
      }
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Signs out the current user and cleans up the store state
   * @returns {Promise<boolean>} Success status
   */
  const signOut = async () => {
    try {
      loading.value = true
      error.value = null

      await auth.signOut()

      // Close persistent DuckDB connection
      await closeConnection();

      // Reset store state
      currentUser.value = null

      return true
    } catch (err) {
      logger.error('Sign out error:', err)
      error.value = err.message || 'Failed to sign out'
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Checks token validity for Google Search Console connection
   * @param {string} email - The email to check
   * @returns {Promise<Object>} Token validity status
   */
  const checkTokenValidity = async (email) => {
    try {
      connectionStatus.value[email].checking = true
      logger.log("Checking token validity for", email)
      const result = await fetchWithAuth('/check-token-validity', 'POST', { email })
      connectionStatus.value[email] = {
        ...result,
        checking: false
      }
      return result
    } catch (err) {
      connectionStatus.value[email] = {
        valid: false,
        checking: false,
        error: err.message,
        lastChecked: new Date().toISOString()
      }
      return null
    }
  }

  /**
   * Initiates OAuth flow for Google Search Console
   */
  const initiateGoogleOAuth = async () => {
    logger.log('initiateGoogleOAuth', apiConfig.redirectUri)
    // The server issues the state and checks it at /oauth2callback, so only a
    // flow started here, by a signed-in user, can store an owner's token.
    const { state } = await fetchWithAuth('/oauth2/state', 'POST')

    const oauth2Endpoint = 'https://accounts.google.com/o/oauth2/v2/auth'
    const params = {
      client_id: apiConfig.googleClientId,
      redirect_uri: apiConfig.redirectUri,
      response_type: 'code',
      scope: 'https://www.googleapis.com/auth/webmasters https://www.googleapis.com/auth/webmasters.readonly',
      access_type: 'offline',
      prompt: 'consent',
      force_refresh: 'true',
      include_granted_scopes: 'true',
      state: state,
    }

    const urlParams = new URLSearchParams(params)
    window.location.href = `${oauth2Endpoint}?${urlParams.toString()}`
  }

  /**
   * Retrieves the current user from Firebase authentication
   * @returns {Promise<Object>} The current user object
   */
  const getCurrentUser = async () => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        resolve(user);
      }, reject);
    });
  }

  // DuckDB state
  const duckDBInstance = ref(null);
  const isDBLoading = ref(false);
  const dbError = ref(null);
  const webMetricsLoaded = ref(false);
  const discoverMetricsLoaded = ref(false);

  // Track registered parquet virtual file names per type (used for VIEW creation)
  const registeredParquetFiles = ref({ web: [], discover: [] });


  /**
   * Lists Parquet files in a specific metrics directory
   * Files have random names but are organized by type folder
   * @param {string} type - The type of metrics ('web' or 'discover')
   * @returns {Promise<Array>} Array of parquet file names
   */
  const listParquetFiles = async (type) => {
    const dirPath = `metrics/${type}`;

    try {
      if (!storage) {
        throw new Error('Firebase storage not initialized');
      }

      const dirRef = storageRef(storage, dirPath);
      if (!dirRef) {
        throw new Error('Failed to create storage reference');
      }

      // Use list() with pagination support instead of listAll()
      let allFiles = [];
      let pageToken = undefined;

      do {
        const result = await list(dirRef, {
          maxResults: 100,
          pageToken: pageToken
        });

        if (result && result.items) {
          allFiles = allFiles.concat(result.items);
        }

        pageToken = result.nextPageToken;

        if (pageToken) {
          logger.log(`📄 Loading next page of files for ${dirPath}...`);
        }
      } while (pageToken);

      // Get all parquet files
      const parquetFiles = allFiles
        .filter(item => item.name.endsWith('.parquet'))
        .map(item => item.name)
        .sort();

      logger.log(`📁 Found ${parquetFiles.length} files in ${dirPath}: ${parquetFiles.join(', ')}`);
      return parquetFiles;
    } catch (error) {
      logger.log(`❌ Error listing ${type} files: ${error.message}`);
      return [];
    }
  };

  /**
   * Fetches Parquet files from Firebase Storage
   * @param {string} type - The type of metrics ('web' or 'discover')
   * @param {Array} fileList - Array of file names to fetch
   */
  const fetchParquetFiles = async (type, fileList) => {
    const results = await Promise.all(
      fileList.map(async (fileName) => {
        try {
          if (!storage) {
            throw new Error('Firebase storage not initialized');
          }

          const fileRef = storageRef(storage, `metrics/${type}/${fileName}`);
          if (!fileRef) {
            throw new Error('Failed to create storage reference');
          }

          const blob = await getBlob(fileRef);
          if (!blob || blob.size === 0) {
            throw new Error('Downloaded file is empty');
          }

          return { name: fileName, blob };
        } catch (error) {
          logger.log(`❌ Failed to fetch ${fileName}: ${error.message}`);
          throw error;
        }
      })
    );

    return results;
  };

  /**
   * Check data loading status.
   * Returns registered parquet file counts (instant) rather than row counts
   * to avoid scanning all parquet files just for a status check.
   */
  const checkDataStatus = async () => {
    return {
      web: registeredParquetFiles.value.web.length,
      discover: registeredParquetFiles.value.discover.length
    };
  };

  /** Initialize DuckDB WASM instance (delegates to duckDBManager). */
  const initializeDuckDB = () => _initDuckDB(duckDBInstance);

  /**
   * Load web metrics data into DuckDB
   */
  const loadWebMetricsData = async () => {
    if (!duckDBInstance.value) {
      await initializeDuckDB();
    }

    // Already loaded — VIEW exists with registered files
    if (registeredParquetFiles.value.web.length > 0) {
      logger.log(`✅ Web metrics already loaded (${registeredParquetFiles.value.web.length} parquet files registered)`);
      webMetricsLoaded.value = true;
      return;
    }

    isDBLoading.value = true;
    logger.log('Loading web metrics data...');

    try {
      await loadMetricsDataWithCache('web');
      webMetricsLoaded.value = true;
      isDBLoading.value = false;
      logger.log('✅ Web metrics data loading complete');
    } catch (error) {
      isDBLoading.value = false;
      logger.log(`❌ Error loading web metrics data: ${error.message}`);
      throw error;
    }
  };

  /**
   * Load discover metrics data into DuckDB
   */
  const loadDiscoverMetricsData = async () => {
    if (!duckDBInstance.value) {
      await initializeDuckDB();
    }

    // Already loaded — VIEW exists with registered files
    if (registeredParquetFiles.value.discover.length > 0) {
      logger.log(`✅ Discover metrics already loaded (${registeredParquetFiles.value.discover.length} parquet files registered)`);
      discoverMetricsLoaded.value = true;
      return;
    }

    isDBLoading.value = true;
    logger.log('Loading discover metrics data...');

    try {
      await loadMetricsDataWithCache('discover');
      discoverMetricsLoaded.value = true;
      isDBLoading.value = false;
      logger.log('✅ Discover metrics data loading complete');
    } catch (error) {
      isDBLoading.value = false;
      logger.log(`❌ Error loading discover metrics data: ${error.message}`);
      throw error;
    }
  };

  /** Get or create a persistent DuckDB connection (delegates to duckDBManager). */
  const getConnection = () => _getConnection(duckDBInstance);

  /** Execute a SQL query against DuckDB (delegates to duckDBManager). */
  const queryDuckDB = (sql, params) => _queryDuckDB(duckDBInstance, sql, params);


  /**
   * Creates a VIEW over all registered parquet files for a given type.
   * Downstream queries use the same names (web_metrics / discover_metrics)
   * so all existing SQL works unchanged.
   */
  const createMetricsView = async (conn, type) => {
    const files = registeredParquetFiles.value[type];
    if (files.length === 0) return;

    const fileList = files.map(f => `'${escapeSql(f)}'`).join(', ');

    if (type === 'discover') {
      await conn.query(`
        CREATE OR REPLACE VIEW discover_metrics AS
        SELECT
          CAST(date AS TIMESTAMP) AS date,
          site_url,
          search_type,
          CAST(impressions AS BIGINT) AS impressions,
          CAST(clicks AS BIGINT) AS clicks,
          CAST(ctr AS DOUBLE) AS ctr
        FROM parquet_scan([${fileList}])
      `);
    } else if (type === 'web') {
      await conn.query(`
        CREATE OR REPLACE VIEW web_metrics AS
        SELECT
          device,
          CAST(impressions AS BIGINT) AS impressions,
          site_url,
          search_type,
          CAST(clicks AS BIGINT) AS clicks,
          CAST(date AS TIMESTAMP) AS date,
          CAST(position AS DOUBLE) AS position,
          CAST(ctr AS DOUBLE) AS ctr
        FROM parquet_scan([${fileList}])
      `);
    }

    logger.log(`✅ ${type}: VIEW created over ${files.length} parquet file(s)`);
  };

  /**
   * Register parquet file buffers with DuckDB and create/update the VIEW.
   * No data is materialized into memory — DuckDB reads directly from the
   * registered parquet files when queries hit the VIEW.
   * @param {Object} conn - DuckDB connection
   * @param {string} type - 'web' or 'discover'
   * @param {Array} files - Array of {fileName, blob} objects
   */
  const loadFilesIntoDuckDB = async (conn, type, files) => {
    const newVirtualNames = [];

    // Register file buffers sequentially to avoid holding all ArrayBuffers in memory at once
    const results = [];
    for (const file of files) {
      try {
        const buffer = await file.blob.arrayBuffer();
        const virtualFileName = `cached_${type}_${file.fileName}`;

        await duckDBInstance.value.registerFileBuffer(virtualFileName, new Uint8Array(buffer));
        newVirtualNames.push(virtualFileName);

        // Release blob reference so GC can reclaim it
        file.blob = null;

        results.push({ success: true, fileName: file.fileName });
      } catch (error) {
        results.push({ success: false, fileName: file.fileName, error: error.message });
      }
    }

    // Add newly registered files to tracking (deduplicated)
    const currentFiles = registeredParquetFiles.value[type];
    const uniqueNew = newVirtualNames.filter(f => !currentFiles.includes(f));
    if (uniqueNew.length > 0) {
      registeredParquetFiles.value[type] = [...currentFiles, ...uniqueNew];
    }

    // Create or replace the VIEW to include all registered files
    await createMetricsView(conn, type);

    const failed = results.filter(r => !r.success);
    if (failed.length > 0) {
      failed.forEach(f => logger.log(`❌ Failed to register ${f.fileName}: ${f.error}`));
    }
  };

  /**
   * Download and load new files that aren't in cache
   * @param {Object} conn - DuckDB connection
   * @param {string} type - 'web' or 'discover'
   * @param {Array} newFileNames - Array of file names to download
   */
  const downloadAndLoadNewFiles = async (conn, type, newFileNames) => {
    // Download all new files in parallel
    const downloadPromises = newFileNames.map(async (fileName) => {
      try {
        const parquetFiles = await fetchParquetFiles(type, [fileName]);
        const file = parquetFiles[0];

        // Cache the downloaded file (corrected parameters)
        await cacheParquetFile(type, fileName, file.blob);

        return {
          fileName: fileName,
          blob: file.blob
        };
      } catch (error) {
        logger.log(`❌ Failed to download ${fileName}: ${error.message}`);
        return null;
      }
    });

    const downloadedFiles = (await Promise.all(downloadPromises)).filter(f => f !== null);

    // Load the newly downloaded files into DuckDB
    if (downloadedFiles.length > 0) {
      await loadFilesIntoDuckDB(conn, type, downloadedFiles);
      logger.log(`✅ ${type}: Downloaded and loaded ${downloadedFiles.length} new files`);
    }
  };

  /**
   * Clear all cached data from IndexedDB
   * Deletes both MetricsParquetCache and WebMetricsDB databases completely
   * @returns {Promise<boolean>} Success status
   */

  /**
   * Load metrics data for a specific type with IndexedDB caching.
   * Strategy:
   * 1. Load ALL cached parquet blobs from IndexedDB in bulk (fast)
   * 2. Register them as virtual files in DuckDB and create a VIEW (no data copied to memory)
   * 3. List the bucket and reconcile: evict cached files no longer present (stale orphans)
   * 4. Download, cache, and register files new to the cache; recreate VIEW
   * @param {string} type - The type of metrics ('web' or 'discover')
   */
  const loadMetricsDataWithCache = async (type) => {
    if (!duckDBInstance.value) return;

    const conn = await getConnection();

    try {
      // STEP 1: Get cached files from IndexedDB (only the requested type)
      let cachedFiles = await getAllCachedFiles(type);
      const cachedCount = cachedFiles.length;

      // STEP 2: Register all cached files and create VIEW
      let cachedFileNames = new Set();
      if (cachedFiles.length > 0) {
        // Extract file names before loading (loading nulls out blob refs)
        cachedFileNames = new Set(cachedFiles.map(f => f.fileName));
        await loadFilesIntoDuckDB(conn, type, cachedFiles);
        logger.log(`✅ ${type}: Registered ${cachedCount} cached files into VIEW`);
      }
      // Release blob array so GC can reclaim memory during download phase
      cachedFiles = null;

      // STEP 3: Always check Firebase Storage for new files
      const firebaseFiles = await listParquetFiles(type);
      if (firebaseFiles.length === 0) return;

      // STEP 4: Reconcile the cache against the live bucket listing.
      // Reaching here means the listing was non-empty (Firebase is reachable),
      // so any cached file absent from it is a stale orphan that would
      // double-count its rows in the unioned VIEW.
      const { toDownload, toEvict } = planParquetCacheSync(cachedFileNames, firebaseFiles);
      logger.log(`🔍 ${type}: bucket ${firebaseFiles.length}, cached ${cachedCount}, download ${toDownload.length}, evict ${toEvict.length}`);

      // Evict stale orphans first, then rebuild the VIEW over what remains.
      if (toEvict.length > 0) {
        for (const fileName of toEvict) {
          const virtualFileName = `cached_${type}_${fileName}`;
          try {
            await duckDBInstance.value.dropFile(virtualFileName);
          } catch (err) {
            logger.warn(`Failed to drop ${virtualFileName}: ${err.message}`);
          }
          registeredParquetFiles.value[type] = registeredParquetFiles.value[type]
            .filter(f => f !== virtualFileName);
        }
        await deleteCachedFiles(type, toEvict);
        await createMetricsView(conn, type);
        logger.log(`🧹 ${type}: Evicted ${toEvict.length} stale file(s) absent from bucket: ${toEvict.join(', ')}`);
      }

      // Download and register files that are new to the cache.
      if (toDownload.length > 0) {
        logger.log(`📥 New files to download: ${toDownload.join(', ')}`);
        await downloadAndLoadNewFiles(conn, type, toDownload);
      } else {
        logger.log(`✅ ${type}: No new files to download, using cached data`);
      }

    } catch (error) {
      logger.log(`❌ Failed to load ${type}: ${error.message}`);
      throw error;
    }
  };

  /**
   * Load all metrics data (both web and discover) with IndexedDB caching
   * Used for backward compatibility and when both datasets are needed
   */
  const loadAllMetricsDataWithCache = async () => {
    if (!duckDBInstance.value) return;

    const types = ['web', 'discover'];

    for (const type of types) {
      try {
        await loadMetricsDataWithCache(type);
      } catch (error) {
        logger.log(`❌ Failed to load ${type}: ${error.message}`);
      }
    }

    // Final status check
    const finalStatus = await checkDataStatus();
    logger.log(`✅ Load complete (web: ${finalStatus.web}, discover: ${finalStatus.discover})`);
  };

  /**
   * Clear IndexedDB cache for a specific type, or the entire database.
   * @param {string} [type] - 'web' or 'discover'. If omitted, deletes the whole DB.
   */
  const clearCacheForType = async (type) => {
    if (!type) {
      await clearIndexedDBCache();
      registeredParquetFiles.value = { web: [], discover: [] };
      webMetricsLoaded.value = false;
      discoverMetricsLoaded.value = false;
      return;
    }

    await clearCachedFilesByType(type);
    logger.log(`🗑️ Cleared IndexedDB cache for type: ${type}`);

    // Reset in-memory state so next load re-fetches
    registeredParquetFiles.value[type] = [];
    if (type === 'web') webMetricsLoaded.value = false;
    if (type === 'discover') discoverMetricsLoaded.value = false;
  };

  /**
   * Refresh metrics data - checks for new files and loads them.
   * This can be called on app boot or manually by the user.
   * @param {string} [type] - Optional type to refresh ('web', 'discover', or undefined for both)
   * @param {Object} [options]
   * @param {boolean} [options.force=false] - When true, clears the cache first so data is re-downloaded
   */
  const refreshMetricsData = async (type = undefined, { force = false } = {}) => {
    if (!duckDBInstance.value) {
      await initializeDuckDB();
    }

    if (force) {
      await clearCacheForType(type);
      if (type) {
        registeredParquetFiles.value[type] = [];
      } else {
        registeredParquetFiles.value = { web: [], discover: [] };
      }
      // Reset loaded flags so load functions re-run fully
      if (!type || type === 'web') webMetricsLoaded.value = false;
      if (!type || type === 'discover') discoverMetricsLoaded.value = false;
    }

    isDBLoading.value = true;
    try {
      if (type === 'web') {
        await loadWebMetricsData();
      } else if (type === 'discover') {
        await loadDiscoverMetricsData();
      } else {
        await loadAllMetricsDataWithCache();
      }
    } finally {
      isDBLoading.value = false;
    }
  };

  return {
    // State
    loading,
    error,
    currentUser,
    connectionStatus,

    // Config
    apiConfig,

    // Auth methods
    signIn,
    signOut,

    // Google Search Console methods
    checkTokenValidity,
    initiateGoogleOAuth,

    // API methods
    fetchWithAuth,
    getCurrentUser,

    // DuckDB methods
    duckDBInstance,
    isDBLoading,
    dbError,
    webMetricsLoaded,
    discoverMetricsLoaded,
    initializeDuckDB,
    loadWebMetricsData,
    loadDiscoverMetricsData,
    queryDuckDB,
    checkDataStatus,
    refreshMetricsData,

    // Parquet file methods
    listParquetFiles,
    fetchParquetFiles,

    // IndexedDB methods
    clearIndexedDBCache
  }
}) 